import { useQuery } from '@tanstack/react-query'
import { useRedirectOnNotFound } from '../lib/useRedirectOnNotFound'
// Country list + SVG flag set, sourced from the already-installed
// `react-phone-number-input` package — same source the Companies-page
// `CountryCell` uses, so the Country row in this modal renders the
// exact same flag + label as the table row the user opened it from.
import countryLabels from 'react-phone-number-input/locale/en.json'
import Flags from 'react-phone-number-input/flags'
// `formatPhoneNumberIntl` turns "+919828282282" into "+91 98282 82282"
// (international format with libphonenumber's per-country grouping).
// Same library `PhoneInputField` already uses to parse + validate, so
// the display format here matches what the input renders while a
// user is typing into it on Profile / Members / Sign-up.
import { formatPhoneNumberIntl } from 'react-phone-number-input'
import { Modal } from './ui/Modal'
import { Button } from './ui/Button'
import { RolePill, StatusPill } from './ui/Pills'
import { Avatar } from './Avatar'
import { api, extractApiError } from '../lib/api'
import { formatDateTime } from '../lib/format'

const ROLE_LABEL = {
  super_admin: 'Master Admin',
  company_admin: 'Company Admin',
  sub_admin: 'Sub Admin',
  user: 'User',
}
const WORK_ROLE_LABEL = {
  manager: 'Manager',
  team_leader: 'Team Leader',
  employee: 'Employee',
}
const STATUS_LABEL = {
  active: 'Active',
  invited: 'Invited',
  suspended: 'Suspended',
  offboarded: 'Offboarded',
}
/** Legacy fallback for tenants signed up before the `country` field
 *  shipped — proxy from `billingCurrency` so the row still reads
 *  something useful (INR → IN India, AED → AE UAE, USD → 🌐 Global).
 *  Matches the CountryCell mapping on the Companies page so the two
 *  surfaces stay consistent for the same tenant. */
const COUNTRY_META_BY_CURRENCY = {
  INR: { code: 'IN', label: 'India' },
  AED: { code: 'AE', label: 'UAE' },
  USD: { code: 'GLOBAL', label: 'Global' },
}

/** Plan pill — small uppercase accent chip, matches the Plan column
 *  on the Companies + AI Usage tables. Shows the catalogue NAME ("Gold
 *  Plan"), falling back to the raw code only when the tenant sits on a plan
 *  that has since left the catalogue, and to "—" when it has no plan.
 *
 *  It used to render `planTier` — the machine code — so a company assigned
 *  "Gold Plan" showed something like "A00_0000…" here while every other
 *  surface showed the name (QA, 2026-09-01). */
function PlanPill({ plan }) {
  if (!plan) return <span className="text-[color:var(--color-ink-muted)]">—</span>
  const label = plan
  return (
    <span className="inline-flex text-xs uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] border border-[color:var(--color-accent-border)]">
      {label}
    </span>
  )
}

/** Tenant status pill — solid filled pill with white text, matches
 *  the Status column on the Companies table. Active = success green,
 *  suspended = priority red, offboarded = slate ink-muted. */
function TenantStatusPill({ status }) {
  if (!status) return <span className="text-[color:var(--color-ink-muted)]">—</span>
  const label = STATUS_LABEL[status] ?? status
  const tone =
    status === 'active'
      ? 'bg-[color:var(--color-success)] dark:bg-white/[0.06] dark:text-emerald-300'
      : status === 'offboarded'
        ? 'bg-[color:var(--color-ink-muted)] dark:bg-white/[0.06] dark:text-slate-300'
        : 'bg-[color:var(--color-priority-urgent)] dark:bg-white/[0.06] dark:text-rose-300'
  return (
    <span
      className={
        'inline-flex w-fit items-center rounded-full px-3 py-1 text-xs font-semibold capitalize text-white border border-transparent dark:border-white/10 ' +
        tone
      }
    >
      {label}
    </span>
  )
}

/** Two-tier resolution: real country code first, billing-currency
 *  proxy second. Returns null when neither is set so the Row falls
 *  back to "—". Renders inline with the flag SVG so the modal reads
 *  the same way the Companies-table Country column does. */
function CountryValue({ country, billingCurrency }) {
  // Tier 1: real ISO code on the tenant.
  if (country && typeof country === 'string') {
    const iso = country.toUpperCase()
    const label = countryLabels[iso] ?? iso
    const FlagSvg = Flags[iso]
    return (
      <span
        className="inline-flex items-center gap-2"
        title={label}
      >
        {FlagSvg ? (
          <FlagSvg
            title={label}
            style={{ width: 18, height: 12, flexShrink: 0 }}
          />
        ) : (
          <span
            aria-hidden="true"
            className="inline-flex items-center justify-center w-[18px] h-[12px] text-[10px]"
          >
            🌐
          </span>
        )}
        <span>{label}</span>
      </span>
    )
  }
  // Tier 2: billing-currency proxy for legacy rows.
  const meta = COUNTRY_META_BY_CURRENCY[billingCurrency]
  if (!meta) {
    // Neither tier resolved — render the same em-dash placeholder
    // the other Rows use for missing values so the modal stays
    // visually consistent.
    return <span className="text-[color:var(--color-ink-muted)]">—</span>
  }
  const FlagSvg = meta.code === 'GLOBAL' ? null : Flags[meta.code]
  return (
    <span className="inline-flex items-center gap-2" title={meta.label}>
      {FlagSvg ? (
        <FlagSvg
          title={meta.label}
          style={{ width: 18, height: 12, flexShrink: 0 }}
        />
      ) : (
        <span
          aria-hidden="true"
          className="inline-flex items-center justify-center w-[18px] h-[12px] text-[10px]"
        >
          🌐
        </span>
      )}
      <span>{meta.label}</span>
    </span>
  )
}

/** Render a stored E.164 phone number in human-readable international
 *  form. "+919828282282" → "+91 98282 82282". The library returns an
 *  empty string when the number can't be parsed (corrupt data, very
 *  old rows that pre-date the PhoneInputField switch), in which case
 *  we fall back to the raw stored value rather than show "" — better
 *  to display an unformatted number than blank the field out. */
function formatContactNumber(raw) {
  if (!raw) return '—'
  const formatted = formatPhoneNumberIntl(raw)
  return formatted || raw
}

/**
 * Read-only profile sheet for a single user, opened from the All Users
 * row kebab menu. Pure presentation — every mutation lives on the menu
 * itself so this modal stays safe to open and close without side
 * effects.
 *
 * Pass `userId={null}` to keep the modal closed; passing a userId
 * triggers the fetch and opens the modal.
 */
export function PlatformUserDetailModal({ userId, onClose }) {
  const open = userId !== null
  const detailQuery = useQuery({
    queryKey: ['platform-user-detail', userId],
    enabled: open,
    queryFn: async () => {
      const res = await api.get(`/super-admin/platform/users/${userId}`)
      return res.data
    },
  })
  // If the user is deleted (or inaccessible) while this modal is open, close it
  // with a notice instead of showing a dead error inside the modal.
  useRedirectOnNotFound(detailQuery, onClose, 'This user is no longer available.')

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="User Details"
      size="ml"
      footer={<Button onClick={onClose}>Close</Button>}
    >
      {detailQuery.isLoading ? (
        <LoadingBody />
      ) : detailQuery.isError ? (
        <p className="text-sm text-[color:var(--color-priority-urgent)]">
          {extractApiError(detailQuery.error).message}
        </p>
      ) : detailQuery.data ? (
        <Body data={detailQuery.data} />
      ) : null}
    </Modal>
  )
}

/**
 * Placeholder that mirrors `Body`'s footprint — identity block plus the
 * Account (6 rows) and Company (4 rows) sections.
 *
 * Why not a one-line "Loading user…": the dialog sizes to its content, so
 * a single line opened it as a ~160px box that then jumped to a
 * near-full-height sheet when the fetch landed. On a phone those read as
 * two different popups appearing in sequence, which is exactly how it was
 * reported (QA, 2026-09-01). Matching the loaded shape means the dialog
 * opens once, at the size it keeps.
 *
 * `aria-busy` + the visually-hidden label keep the state announced now
 * that there is no longer a literal "Loading user…" string on screen.
 */
function LoadingBody() {
  const bar = 'rounded bg-[color:var(--color-rule)]'
  const rows = (n) =>
    Array.from({ length: n }, (_, i) => (
      <div key={i} className="flex items-center justify-between gap-4 px-4 py-2.5">
        <span className={`${bar} h-3 w-28`} />
        <span className={`${bar} h-3 w-24`} />
      </div>
    ))
  return (
    <div className="space-y-5 animate-pulse" aria-busy="true">
      <span className="sr-only">Loading user…</span>
      <div className="flex items-center gap-4">
        <span className={`${bar} h-12 w-12 rounded-full`} />
        <div className="min-w-0 space-y-2">
          <span className={`${bar} block h-4 w-40`} />
          <span className={`${bar} block h-3 w-52`} />
        </div>
      </div>
      {[
        { title: 'Account', count: 6 },
        { title: 'Company', count: 4 },
      ].map((s) => (
        <div key={s.title}>
          <h3 className="mt-0 mb-2 text-sm uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)]">
            {s.title}
          </h3>
          <div className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] divide-y divide-[color:var(--color-rule)]">
            {rows(s.count)}
          </div>
        </div>
      ))}
    </div>
  )
}

function Body({ data }) {
  return (
    <div className="space-y-5">
      {/* Identity block */}
      <div className="flex items-center gap-4">
        <Avatar name={data.fullName} avatarUrl={data.avatarUrl ?? null} size="lg" />
        <div className="min-w-0">
          <p className="text-lg font-semibold text-[color:var(--color-ink-strong)] truncate">
            {data.fullName}
          </p>
          <p className="text-sm text-[color:var(--color-ink-muted)] truncate">
            {data.emailId}
          </p>
        </div>
      </div>

      {/* Account */}
      <Section title="Account">
        {/* Role + Status render as pills so they match the All Users
         *  table — keeps Role / Status reading the same whether the
         *  admin is looking at the table row or this detail modal. */}
        <Row
          label="Role"
          value={
            <RolePill
              label={ROLE_LABEL[data.role] ?? data.role}
              tone={data.role === 'user' ? 'violet' : 'amber'}
            />
          }
        />
        {data.role === 'user' ? (
          <Row
            label="Work role"
            value={
              data.workRoleName ?? WORK_ROLE_LABEL[data.workRole] ?? data.workRole
            }
          />
        ) : null}
        <Row
          label="Contact number"
          value={formatContactNumber(data.contactNumber)}
        />
        <Row label="Created" value={formatDateTime(data.createdAt)} />
        <Row label="Last active" value={formatDateTime(data.lastActiveAt)} />
        <Row
          label="Password last changed"
          value={formatDateTime(data.passwordChangedAt)}
        />
        <Row
          label="Status"
          value={
            <StatusPill
              status={data.status}
              companyStatus={data.tenant?.status}
            />
          }
        />
      </Section>

      {/* Company — only meaningful for non-platform users */}
      {data.tenant ? (
        <Section title="Company">
          <Row label="Name" value={data.tenant.name} />
          {/* Country — flag + name, sourced from the tenant's real
           *  ISO code (signup-time IP + Stripe billing-address
           *  overwrite). Falls back to the billing-currency proxy for
           *  legacy rows where `country` is still null, matching the
           *  Companies-page Country column so the two surfaces never
           *  disagree on a given tenant. */}
          <Row
            label="Country"
            value={
              <CountryValue
                country={data.tenant.country}
                billingCurrency={data.tenant.billingCurrency}
              />
            }
          />
          {/* Plan + Status render as styled pills so the modal matches
           *  the AI Usage / Companies / All Users tables — every other
           *  surface across the platform-admin chrome shows Plan as a
           *  small uppercase accent chip and Status as a leading-dot
           *  coloured pill, so plain lowercase text here read as a
           *  drift bug ("STAR" in the table, "star" in the modal). */}
          <Row
            label="Plan"
            value={
              <PlanPill plan={data.tenant.planName ?? data.tenant.planTier} />
            }
          />
          <Row
            label="Status"
            value={<TenantStatusPill status={data.tenant.status} />}
          />
        </Section>
      ) : null}
    </div>
  )
}

function Section({ title, children }) {
  return (
    <div>
      <h3 className="mt-0 mb-2 text-sm uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)]">
        {title}
      </h3>
      <div className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] divide-y divide-[color:var(--color-rule)]">
        {children}
      </div>
    </div>
  )
}

function Row({ label, value }) {
  return (
    <div className="grid grid-cols-[160px_minmax(0,1fr)] gap-3 px-4 py-2.5">
      {/* Label + value share the same `text-sm` size so both columns
       *  read at the same scale. Label stays in `ink-muted` to keep
       *  its supporting role but matches the value's font size, in
       *  step with the same fix on the Company Dashboard Row. */}
      <span className="text-sm text-[color:var(--color-ink-muted)] self-center">
        {label}
      </span>
      <span className="text-sm text-[color:var(--color-ink-strong)] min-w-0 break-words">
        {value}
      </span>
    </div>
  )
}
